import React from 'react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-theme-bg-primary py-20">
      <div className="container mx-auto px-6 max-w-4xl">
        <h1 className="text-4xl font-bold mb-12 text-center text-white">Terms of Sale & Warranty</h1>
        
        {/* Terms of Sale Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-theme-primary-500">Terms of Sale</h2>
          
          <div className="space-y-8">
            <div className="bg-theme-bg-secondary/30 backdrop-blur-sm rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4 text-white">1. Securing Your Build</h3>
              <p className="text-gray-300 leading-relaxed">
                Once the specifications for your custom instrument have been discussed and finalized, a non-refundable down payment is required to secure your build slot. Your initial payment reserves your place in the building queue and the specific materials, such as the selected wood for your guitar.
              </p>
            </div>

            <div className="bg-theme-bg-secondary/30 backdrop-blur-sm rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4 text-white">2. Payment Schedule</h3>
              <p className="text-gray-300 leading-relaxed mb-4">We offer two payment options:</p>
              <ul className="text-gray-300 leading-relaxed space-y-2 ml-4">
                <li><strong>Two-Part Payment:</strong> A 30% non-refundable down payment is required to commence the build, with the remaining 70% balance due upon completion.</li>
                <li><strong>Installment Plan:</strong> The total cost can be divided into three equal, non-refundable payments made over the course of the build (with the last one upon completion).</li>
              </ul>
              <p className="text-gray-300 leading-relaxed mt-4">
                Upon completion of your instrument, we will provide photos of the complete guitar before requesting the final balance payment and before shipping.
              </p>
              <div className="bg-yellow-900/20 border-l-4 border-yellow-500 p-4 mt-4">
                <p className="text-yellow-200 font-medium">
                  <strong>Please note:</strong> All payments are non-refundable. Should a customer be unable to complete the payment schedule, any funds paid will be forfeited. This policy is in place because payments are immediately allocated to materials, hardware, and labor for your custom instrument.
                </p>
              </div>
            </div>

            <div className="bg-theme-bg-secondary/30 backdrop-blur-sm rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4 text-white">3. Accepted Payment Methods</h3>
              <ul className="text-gray-300 leading-relaxed space-y-3">
                <li>
                  <strong>Wire Transfer:</strong> Instructions for wire transfers are provided on the second page of your invoice. Please instruct your bank to ensure all transfer fees are covered by the sender (select the 'OUR' fee option). All transfers must be made in USD; do not convert the payment to our local currency.
                </li>
                <li>
                  <strong>PayPal:</strong> If you prefer to use PayPal, please inform us beforehand. A 4% processing fee will be added to your invoice, and we will send you a formal payment request.
                </li>
              </ul>
            </div>

            <div className="bg-theme-bg-secondary/30 backdrop-blur-sm rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4 text-white">4. Taxes, Duties, and Customs</h3>
              <p className="text-gray-300 leading-relaxed">
                All prices are exclusive of any applicable taxes, VAT, or customs fees that may be incurred in your country. The buyer is solely responsible for these charges.
              </p>
            </div>

            <div className="bg-theme-bg-secondary/30 backdrop-blur-sm rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4 text-white">5. Production Timeline</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                The estimated lead time for your custom instrument is 6-8 months, beginning from the date the initial down payment is successfully received by VitaeGuitars.
              </p>
              <div className="bg-blue-900/20 border-l-4 border-blue-500 p-4">
                <p className="text-blue-200 text-sm">
                  <strong>Note:</strong> The lead time may change in the future, depending on the volume of orders. If so, it will be changed for new orders only and with advance notice.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Warranty Section */}
        <section>
          <h2 className="text-3xl font-bold mb-8 text-theme-primary-500">Limited Lifetime Warranty</h2>
          
          <div className="space-y-8">
            <div className="bg-theme-bg-secondary/30 backdrop-blur-sm rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4 text-white">1. Our Commitment to Quality</h3>
              <p className="text-gray-300 leading-relaxed">
                VitaeGuitars stands proudly behind the craftsmanship of every instrument we build. Your instrument is accompanied by a Certificate of Authenticity. For any verification inquiries, please contact us directly.
              </p>
            </div>

            <div className="bg-theme-bg-secondary/30 backdrop-blur-sm rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4 text-white">2. Warranty Coverage</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                VitaeGuitars warrants the instruments to be free from defects in materials and workmanship for the lifetime of the original purchaser, subject to the limitations and exclusions outlined below.
              </p>
              <ul className="text-gray-300 leading-relaxed space-y-2">
                <li><strong>Original Purchaser:</strong> This warranty is extended exclusively to the original retail purchaser and is non-transferable to subsequent owners.</li>
                <li><strong>Used Instruments:</strong> Pre-owned instruments sold directly by VitaeGuitars are covered by a separate limited 3-month warranty.</li>
              </ul>
            </div>

            <div className="bg-theme-bg-secondary/30 backdrop-blur-sm rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4 text-white">3. Limitations and Exclusions</h3>
              <p className="text-gray-300 leading-relaxed mb-4">Warranty does not cover:</p>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3 text-gray-300 text-sm">
                  <div>
                    <strong>• Unauthorized Modifications & Repairs:</strong> Any damage resulting from repairs, modifications, or alterations performed by any party not expressly authorized in writing by VitaeGuitars.
                  </div>
                  <div>
                    <strong>• Damage, Misuse, & Negligence:</strong> Any damage due to accident, misuse, negligence, wear, or improper storage.
                  </div>
                  <div>
                    <strong>• Environmental Factors:</strong> Damage resulting from exposure to extreme humidity, temperature, direct sunlight, or moisture.
                  </div>
                  <div>
                    <strong>• Normal Wear & Tear:</strong> Standard wear and tear, including but not limited to: worn frets, worn nut, strings, plating on hardware, and the natural aging, discoloration, or fading of the finish.
                  </div>
                  <div>
                    <strong>• Routine Maintenance & Setups:</strong> Adjustments related to personal setup preferences, such as truss rod, action (string height), intonation, and pickup height. These are considered routine maintenance, not defects.
                  </div>
                </div>

                <div className="space-y-3 text-gray-300 text-sm">
                  <div>
                    <strong>• Third-Party Components:</strong> Hardware and electronics (e.g., tuners, bridges, pickups) are covered by the warranties of their respective manufacturers.
                  </div>
                  <div>
                    <strong>• Tampering:</strong> Any instrument on which the logo, finish, wood parts has been tampered with, altered, or removed.
                  </div>
                  <div>
                    <strong>• Provenance:</strong> Any instrument not purchased directly from VitaeGuitars or an authorized VitaeGuitars dealer.
                  </div>
                  <div>
                    <strong>• Subjective Qualities:</strong> Subjective preferences regarding the instrument's tone, feel, or aesthetic qualities.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <div className="mt-16 text-center">
          <div className="bg-theme-bg-secondary/30 backdrop-blur-sm rounded-lg p-6">
            <h3 className="text-xl font-semibold mb-4 text-white">Questions?</h3>
            <p className="text-gray-300 mb-4">
              For any questions regarding our terms of sale or warranty, please contact us:
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 text-theme-primary-500">
              <a href="mailto:VitaeGuitars@gmail.com" className="hover:text-theme-primary-400 transition-colors">
                VitaeGuitars@gmail.com
              </a>
              <span className="hidden sm:inline text-gray-400">|</span>
              <a href="tel:+972523484419" className="hover:text-theme-primary-400 transition-colors">
                +972 523484419
              </a>
            </div>
          </div>
        </div>

        {/* Back to Home Link */}
        <div className="text-center mt-8">
          <button 
            onClick={() => { window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); }}
            className="inline-flex items-center px-6 py-3 bg-theme-primary-500 hover:bg-theme-primary-600 text-white rounded-lg transition-colors"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
